import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Box,
  Button,
  InputLabel,
  Select,
  FormControl,
  MenuItem,
  styled,
  alpha,
  InputBase,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Tooltip
} from '@mui/material';
import CustomPagination from 'components/pagination';
import SearchIcon from '@mui/icons-material/Search';
import { Http } from 'api/http/http';
import { logout } from 'store/reducers/auth';
import { openStudentInfoInNewTab } from 'utils/studentInfoUtils';

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25)
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(0),
    width: 'auto'
  }
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    [theme.breakpoints.up('sm')]: {
      width: '12ch',
      '&:focus': {
        width: '20ch'
      }
    }
  }
}));

const columns = [
  { id: 'studentNumber', label: '학번', align: 'center' },
  { id: 'studentName', label: '이름', width: 70, align: 'center' },
  { id: 'departmentName', label: '학과', align: 'center' },
  { id: 'level', label: '과정', width: 50, align: 'center' },
  { id: 'moduleName', label: '모듈명', align: 'center' },
  { id: 'issuedNumber', label: '발급번호', width: 150, align: 'center' },  // certificateNumber에서 issuedNumber로 변경
  { id: 'confirmedAt', label: '승인날짜', minWidth: 100, align: 'center' },  // createdDate에서 confirmedAt으로 변경
  { id: 'pdf', label: '다운로드', minWidth: 50, align: 'center' },
  { id: 'cancel', label: '취소', minWidth: 50, align: 'center' }
];

// 정렬기능을 위한 api 문서의 필드와 컬럼 필드명이랑 매핑시킨 객체
const apiFieldMapping = {
  studentNumber: 'm.studentNumber',
  studentName: 'm.memberName',
  departmentName: 'm.departmentName',
  level: 'md.level',  // process에서 level로 변경
  moduleName: 'md.moduleName',
  issuedNumber: 'issuedNumber',  // certificateNumber에서 issuedNumber로 변경
  confirmedAt: 'confirmedAt'  // createdDate에서 confirmedAt으로 변경
};

export default function AdminCertificate() {
  const rowsPerPage = 20;
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true); // 로딩 상태 추가

  //const [filter, setFilter] = useState('');
  const [moduleFilter, setModuleFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchType, setSearchType] = useState('');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedRowForCancel, setSelectedRowForCancel] = useState(null);

  // 승인 / 거절 상태를 상수로 선언
  const statusOptions = [
    { label: '승인', value: 'approved' },
    { label: '거절', value: 'rejected' }
  ];

  const [studentNumberQuery, setStudentNumberQuery] = useState('');
  const [studentNameQuery, setStudentNameQuery] = useState('');
  const [moduleNameQuery, setModuleNameQuery] = useState('');

  const [sortConfig, setSortConfig] = useState({ field: 'createdAt', direction: 'desc' });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handleLogout = () => dispatch(logout());

  const handleRefreshExpired = () => {
    navigate('/studentLogin');
  };

  const http = new Http({ onRefreshExpired: handleRefreshExpired, onLogout: handleLogout });

  // const handleChange = (event) => setFilter(event.target.value);
  const handleStatusFilterChange = (event) => setStatusFilter(event.target.value);
  const handleTypeChange = (event) => setSearchType(event.target.value);

  const handleChangePage = (event, newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage - 1);
    }
  };

  // 검색 타입 변경 시 상태 업데이트
  const handleSearchChange = (event) => {
    const value = event.target.value;
    switch (searchType) {
      case 10:
        setStudentNameQuery(value);
        break;
      case 20:
        setStudentNumberQuery(value);
        break;
      case 30:
        setModuleNameQuery(value);
        break;
      default:
        return;
    }
  };

  // 정렬기능: 선택된 컬럼에 대해 정렬순서 토글
  const handleSort = (column) => {
    const field = apiFieldMapping[column];
    const isDesc = sortConfig.field === field && sortConfig.direction === 'desc';
    setSortConfig({ field, direction: isDesc ? 'asc' : 'desc' });
  };

  // 모듈 필터링 목록 가져오기
  useEffect(() => {
    const fetchModules = async () => {
      try {
        //const response = await http.getWithToken('/api/admin/modules/filter');
        const response = {
          status: 200,
          data: {
            data: [
              { id: '1', moduleName: '모듈1' },
              { id: '2', moduleName: '모듈2' },
              { id: '3', moduleName: '모듈3' }
            ]
          }
        };
        if (response.status === 200) {
          setModuleFilter(response.data.data);
        } else {
          console.error('데이터를 불러오는데 오류가 발생했습니다. 다시 시도해주세요');
        }
      } catch (error) {
        console.error('데이터를 불러오는데 오류가 발생했습니다. 다시 시도해주세요');
      }
    };

    fetchModules();
  }, []);

  // 이수증 리스트 불러오기
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true); // 로딩 시작
      try {
        let url = `/api/admin/certificates/confirmed?page=${page}&size=${rowsPerPage}`;  // API 엔드포인트 변경

        // 정렬정보를 url에 추가
        if (sortConfig.field) {
          url += `&sort=${sortConfig.field},${sortConfig.direction}`;
        }

        // module 필터 타입이 설정되었다면 url에 추가, 없으면 전체 모듈 id 사용
        // if (filter) {
        //   url += `&moduleFilter=${filter}`;
        // } else {
        //   const allModuleId = moduleFilter.map((module) => module.id).join(',');
        //   url += `&moduleFilter=${allModuleId}`;
        // }

        // 승인/거절 상태를 url에 추가
        const statusFilterParam = statusFilter || 'all';
        url += `&approvalStatus=${statusFilterParam}`;

        const response = await http.getWithToken(url);

        setRows(response.data.result.content);
        setTotalPages(response.data.result.totalPages); // .data.data.totalPages에서 .data.result.totalPages로 변경
      } catch (error) {
        console.error('데이터를 불러오는데 오류가 발생했습니다. 다시 시도해주세요');
      } finally {
        setLoading(false); // 로딩 종료
      }
    };

    fetchData();
  }, [
    page,
    rowsPerPage,
    // filter,
    sortConfig,
    // searchType,
    // studentNameQuery,
    // studentNumberQuery,
    // moduleNameQuery,
    moduleFilter,
    statusFilter
  ]);

  // 검색 실행 함수 추가
  const handleSearch = async () => {
    if (!searchType) return; // 검색 유형이 선택되지 않았으면 리턴

    setLoading(true);
    try {
      let url = `/api/admin/certificates/confirmed?page=0&size=${rowsPerPage}`; // 검색 시 첫 페이지로 이동
      setPage(0);

      // 정렬정보를 url에 추가
      if (sortConfig.field) {
        url += `&sort=${sortConfig.field},${sortConfig.direction}`;
      }

      // 승인/거절 상태를 url에 추가
      const statusFilterParam = statusFilter || 'all';
      url += `&approvalStatus=${statusFilterParam}`;

      // 검색 유형별 url 파라미터 추가
      switch (searchType) {
        case 10:
          if (studentNameQuery) {
            url += `&searchType=name&keyword=${studentNameQuery}`;
          }
          break;
        case 20:
          if (studentNumberQuery) {
            url += `&searchType=studentNumber&keyword=${studentNumberQuery}`;
          }
          break;
        case 30:
          if (moduleNameQuery) {
            url += `&searchType=moduleName&keyword=${moduleNameQuery}`;
          }
          break;
        default:
          break;
      }

      const response = await http.getWithToken(url);

      setRows(response.data.result.content);
      setTotalPages(response.data.result.totalPages);
    } catch (error) {
      console.error('검색 중 오류가 발생했습니다. 다시 시도해주세요');
    } finally {
      setLoading(false);
    }
  };

  // 검색 초기화 함수 추가
  const handleSearchReset = async () => {
    setSearchType('');
    setStudentNameQuery('');
    setStudentNumberQuery('');
    setModuleNameQuery('');
    setPage(0);

    // 초기 데이터 다시 로드
    setLoading(true);
    try {
      let url = `/api/admin/certificates/confirmed?page=0&size=${rowsPerPage}`;

      if (sortConfig.field) {
        url += `&sort=${sortConfig.field},${sortConfig.direction}`;
      }

      const statusFilterParam = statusFilter || 'all';
      url += `&approvalStatus=${statusFilterParam}`;

      const response = await http.getWithToken(url);

      setRows(response.data.result.content);
      setTotalPages(response.data.result.totalPages);
    } catch (error) {
      console.error('데이터를 불러오는데 오류가 발생했습니다. 다시 시도해주세요');
    } finally {
      setLoading(false);
    }
  };

  const handleExportToExcel = async () => {
    try {
      const response = await http.getWithToken('/api/admin/certificates/excel', {}, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', 'module_certificate.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      alert('다운로드 중 오류가 발생했습니다. 잠시 후에 다시 시도해주세요');
    }
  };

  // 검색에 모듈 검색 기능을 추가함으로써 조건문이 추가되어 외부로 꺼냄
  let inputValue = '';
  switch (searchType) {
    case 10:
      inputValue = studentNameQuery;
      break;
    case 20:
      inputValue = studentNumberQuery;
      break;
    case 30:
      inputValue = moduleNameQuery;
      break;
    default:
      inputValue = '';
      break;
  }

  // PDF 다운로드 함수
  const handleDownloadCertificatePdf = async (row) => {
    try {
      const response = await http.postWithToken(
        '/api/admin/certificates/pdf/student-module',
        { studentNumber: row.studentNumber, moduleName: row.moduleName },
        { responseType: 'blob' }
      );
      const file = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL, '이수증 다운로드', 'height=1000, width=800');
    } catch (error) {
      alert('이수증 PDF 다운로드에 실패하였습니다. 잠시 후 다시 시도해주세요.');
    }
  };

  // 취소 불가 사유 판정 (백엔드 AdminCertificateService.cancelCertificate 규칙과 동일)
  //   → 사유가 있으면 애초에 버튼을 누를 수 없게 하고, 그 이유를 툴팁으로 알린다.
  const getCancelBlockReason = (row) => {
    if (row.status === '승인' && !row.isScholarshipAvailable) {
      return '해당 이수증으로 처리된(처리중인) 장학증이 존재하여 취소할 수 없습니다.';
    }
    if (row.status !== '승인' && row.status !== '신청중') {
      return '이미 취소되었거나 반려된 이수증은 취소할 수 없습니다.';
    }
    return null;
  };

  // 취소 확인 팝업 열기
  const handleCancelClick = (row) => {
    setSelectedRowForCancel(row);
    setCancelDialogOpen(true);
  };

  // 취소 확인 팝업 닫기
  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
    setSelectedRowForCancel(null);
  };

  // 취소 실행
  const handleCancelConfirm = async () => {
    if (!selectedRowForCancel) return;

    try {
      await http.postWithToken(`/api/admin/certificates/${selectedRowForCancel.id}/cancel`);
      alert('이수증이 취소되었습니다.');

      // 현재 페이지 데이터 다시 로드
      const currentPage = page;
      setPage(0); // 임시로 0으로 설정
      setTimeout(() => setPage(currentPage), 100); // 원래 페이지로 복원하여 데이터 새로고침

      handleCancelDialogClose();
    } catch (error) {
      console.error('취소 처리 중 오류가 발생했습니다:', error);
      // 백엔드가 내려주는 구체적인 사유(장학증 존재, 이미 취소됨 등)를 그대로 노출한다.
      alert(error.response?.data?.message || '취소 처리 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
      handleCancelDialogClose();
    }
  };

  return (
    <>
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh" sx={{ flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', width: '85%' }}>
          <Box sx={{ width: '70%', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Box sx={{ width: '13%' }}>
              <FormControl fullWidth>
                <InputLabel sx={{ paddingY: 0.25 }} id="demo-simple-select-label">
                  검색 유형
                </InputLabel>
                <Select value={searchType} label="Age" onChange={handleTypeChange} disabled={loading}>
                  <MenuItem value="">선택하지 않음</MenuItem>
                  <MenuItem value={10}>이름</MenuItem>
                  <MenuItem value={20}>학번</MenuItem>
                  <MenuItem value={30}>모듈명</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box>
              <Search>
                <SearchIconWrapper>
                  <SearchIcon />
                </SearchIconWrapper>
                <StyledInputBase
                  placeholder="검색하기…"
                  inputProps={{ 'aria-label': 'search' }}
                  value={inputValue}
                  onChange={handleSearchChange}
                  disabled={loading}
                />
              </Search>
            </Box>
            <Box sx={{ marginLeft: '8px', display: 'flex', gap: '8px' }}>
              <Button
                onClick={handleSearch}
                variant="contained"
                disabled={loading || !searchType}
                size="small"
              >
                검색
              </Button>
              <Button
                onClick={handleSearchReset}
                variant="outlined"
                disabled={loading}
                size="small"
              >
                초기화
              </Button>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              width: '30%',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              alignItems: 'center'
            }}
          >
            {/* <Box sx={{ display: 'flex', width: '30%', marginRight: '16px' }}>
              <FormControl fullWidth>
                <InputLabel sx={{ paddingY: 0.25 }} id="demo-simple-select-label">
                  모듈
                </InputLabel>
                <Select value={filter} label="Age" onChange={handleChange}>
                  <MenuItem value="">선택하지 않음</MenuItem>
                  {moduleFilter.map((module) => (
                    <MenuItem key={module.id} value={module.id}>
                      {module.moduleName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box> */}
            <Box sx={{ width: '40%', marginRight: '16px' }}>
              <FormControl fullWidth>
                <InputLabel sx={{ paddingY: 0.25 }} id="status-select-label">
                  승인/거절
                </InputLabel>
                <Select
                  labelId="status-select-label"
                  id="status-select"
                  value={statusFilter}
                  label="승인/거절"
                  onChange={handleStatusFilterChange}
                  disabled={loading}
                >
                  <MenuItem value="">선택하지않음</MenuItem>
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', width: '50%' }}>
              <Button onClick={handleExportToExcel} variant="contained" disabled={loading}>
                엑셀추출
              </Button>
            </Box>
          </Box>
        </Box>
        <Paper sx={{ width: '85%', margin: '0 auto', flex: 1, overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 'none', overflowX: 'hidden' }}>
            <Table stickyHeader aria-label="sticky table" sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead sx={{ backgroundColor: '#bfbfbf' }}>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      sx={{
                        cursor: loading ? 'default' : 'pointer',
                        opacity: loading ? 0.6 : 1,
                        ...(column.width && { width: column.width, minWidth: column.width, maxWidth: column.width })
                      }}
                      onClick={loading ? undefined : () => handleSort(column.id)}
                    >
                      {column.label}
                      {!loading && sortConfig.field === apiFieldMapping[column.id] ? (sortConfig.direction === 'asc' ? ' 🔼' : ' 🔽') : ''}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} sx={{ textAlign: 'center' }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell sx={{ textAlign: 'center' }} colSpan={columns.length}>
                      현재 목록이 비어있습니다.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => {
                    return (
                      <TableRow hover role="checkbox" tabIndex={-1} key={`${row.createdDate}-${row.certificateNumber}`}>
                        {columns.map((column) => {
                          const value = row[column.id];
                          if (column.id === 'confirmedAt') {  // createdDate에서 confirmedAt으로 변경
                            let date = new Date(value);
                            let formattedDate =
                              date.getFullYear() +
                              '-' +
                              (date.getMonth() + 1).toString().padStart(2, '0') +
                              '-' +
                              date.getDate().toString().padStart(2, '0') +
                              ' ' +
                              date.getHours().toString().padStart(2, '0') +
                              ':' +
                              date.getMinutes().toString().padStart(2, '0') +
                              ':' +
                              date.getSeconds().toString().padStart(2, '0');

                            return (
                              <TableCell key={column.id} align={column.align} sx={{ fontSize: '0.7rem' }}>
                                {formattedDate}
                              </TableCell>
                            );
                          } else if (column.id === 'level') {  // process에서 level로 변경
                            let course;
                            if (value === '초급') {
                              course = '초급';
                            } else if (value === '중급') {
                              course = '중급';
                            } else if (value === '고급') {
                              course = '고급';
                            } else {
                              course = value;
                            }
                            return (
                              <TableCell
                                key={column.id}
                                align={column.align}
                                sx={{ fontSize: '0.7rem', ...(column.width && { width: column.width, minWidth: column.width, maxWidth: column.width }) }}
                              >
                                {course}
                              </TableCell>
                            );
                          } else if (column.id === 'pdf') {
                            return (
                              <TableCell key={column.id} align={column.align} sx={{ fontSize: '0.7rem' }}>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  onClick={() => handleDownloadCertificatePdf(row)}
                                  size="small"
                                >
                                  PDF
                                </Button>
                              </TableCell>
                            );
                          } else if (column.id === 'cancel') {
                            const cancelBlockReason = getCancelBlockReason(row);
                            return (
                              <TableCell key={column.id} align={column.align} sx={{ fontSize: '0.7rem' }}>
                                {/* 비활성 버튼은 이벤트를 발생시키지 않으므로 span 으로 감싸야 툴팁이 뜬다 */}
                                <Tooltip title={cancelBlockReason || ''}>
                                  <span>
                                    <Button
                                      variant="contained"
                                      color="error"
                                      onClick={() => handleCancelClick(row)}
                                      size="small"
                                      disabled={Boolean(cancelBlockReason)}
                                    >
                                      취소
                                    </Button>
                                  </span>
                                </Tooltip>
                              </TableCell>
                            );
                          } else if (column.id === 'studentNumber' || column.id === 'studentName') {
                            return (
                              <TableCell
                                key={column.id}
                                align={column.align}
                                sx={{
                                  fontSize: '0.7rem',
                                  cursor: 'pointer',
                                  color: 'primary.main',
                                  '&:hover': { textDecoration: 'underline' }
                                }}
                                onClick={() => openStudentInfoInNewTab(row.studentNumber)}
                              >
                                {value}
                              </TableCell>
                            );
                          } else {
                            const isLongText = ['departmentName', 'moduleName', 'issuedNumber'].includes(column.id);
                            return (
                              <TableCell
                                key={column.id}
                                align={column.align}
                                sx={{
                                  fontSize: '0.7rem',
                                  ...(isLongText && {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: 0,
                                    whiteSpace: 'nowrap'
                                  })
                                }}
                                title={isLongText ? value : undefined}
                              >
                                {value}
                              </TableCell>
                            );
                          }
                        })}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ marginTop: '16px' }}>
          <CustomPagination count={totalPages} page={page} onChange={handleChangePage} disabled={loading} />
        </Box>
      </Box>

      {/* 취소 확인 팝업 */}
      <Dialog
        open={cancelDialogOpen}
        onClose={handleCancelDialogClose}
        aria-labelledby="cancel-dialog-title"
        aria-describedby="cancel-dialog-description"
      >
        <DialogTitle id="cancel-dialog-title">
          이수증 취소 확인
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="cancel-dialog-description">
            정말로 이 이수증을 취소하시겠습니까?
            <br />
            <strong>학생: {selectedRowForCancel?.studentName}</strong>
            <br />
            <strong>모듈: {selectedRowForCancel?.moduleName}</strong>
            <br />
            <strong>발급번호: {selectedRowForCancel?.issuedNumber}</strong>
            <br />
            <strong>학번: {selectedRowForCancel?.studentNumber}</strong>
            <br />
            <br />
            취소 후에는 복구할 수 없습니다. 동명이인에 주의바랍니다!
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDialogClose} color="primary">
            아니오
          </Button>
          <Button onClick={handleCancelConfirm} color="error" variant="contained" autoFocus>
            취소
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
